/** @jsxImportSource @emotion/react */
import { Tooltip } from 'antd'
import colors from '@sportninja/common/constants/appColors'
import {
  resolveInvitationStatus,
  type InvitationStatusIcon,
  type InvitationStatusTone,
} from '@sportninja/common/features/competition/operations/utils/invitationStatus'
import type { InvitationDeliveryStatus } from '@sportninja/common/features/competition/operations/utils/rosterRows'
import React, { memo } from 'react'
import Text from '../ts-components/Text'
import { css } from '@emotion/react'
import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  InfoCircleOutlined,
  MailOutlined,
  StopOutlined,
} from '@ant-design/icons'

export type { InvitationDeliveryStatus }

interface Props {
  isAccepted?: boolean
  isInvited?: boolean
  deliveryStatus?: InvitationDeliveryStatus | null
  deliveryAt?: string | null
}

const tagStyles = css`
  min-width: 90px;
  text-align: center;
  display: inline-flex;
  justify-content: center;
  align-items: center;
  padding: 4px 8px;
  border-radius: 8px;
  gap: 6px;
  white-space: nowrap;
`

const TONE_STYLE: Record<
  InvitationStatusTone,
  { background: string; border: string }
> = {
  success: {
    background: 'rgba(57, 187, 85, 0.20)',
    border: colors.SUCCESS_200,
  },
  error: {
    background: 'rgba(223, 66, 66, 0.20)',
    border: colors.ERROR_200,
  },
  warning: {
    background: 'rgba(255, 204, 49, 0.20)',
    border: colors.WARNING_200,
  },
  info: {
    background: 'rgba(90, 166, 255, 0.20)',
    border: colors.INFO_200,
  },
  neutral: {
    background: colors.NEUTRAL_100,
    border: colors.NEUTRAL_200,
  },
}

const ICONS: Record<InvitationStatusIcon, React.ReactNode> = {
  check: <CheckCircleOutlined />,
  close: <CloseCircleOutlined />,
  stop: <StopOutlined />,
  info: <InfoCircleOutlined />,
  clock: <ClockCircleOutlined />,
  mail: <MailOutlined />,
}

/**
 * InvitationStatusTag shows where someone is in the invitation flow, including
 * whether the email actually reached them.
 *
 * Before delivery tracking, "Invited" meant only that an email had been queued —
 * a message sitting in someone's inbox and one that bounced off a typo months
 * ago looked identical. This surfaces that difference.
 *
 * Renders nothing when there is no invitation, matching the previous behaviour
 * of the Invite column.
 */
const InvitationStatusTag = ({
  isAccepted,
  isInvited,
  deliveryStatus,
  deliveryAt,
}: Props) => {
  const appearance = resolveInvitationStatus({
    isAccepted,
    isInvited,
    deliveryStatus,
    deliveryAt,
  })

  if (!appearance) {
    return null
  }

  const tone = TONE_STYLE[appearance.tone]

  return (
    <Tooltip title={appearance.tooltip} placement='left'>
      <div
        css={[
          tagStyles,
          css`
            background-color: ${tone.background};
            border: 1px solid ${tone.border};
          `,
        ]}
      >
        {ICONS[appearance.icon]}
        <Text
          variant='paragraphXSmall'
          weight='medium'
          color={colors.NEUTRAL_0}
        >
          {appearance.label}
        </Text>
      </div>
    </Tooltip>
  )
}

export default memo(InvitationStatusTag)
